import { useEffect, useState } from "react";
import {
  BarChart3, Download, Loader2, Users, Sparkles, AlertCircle, Repeat, TrendingUp,
  FileText, Phone, MessageSquare, BellRing,
} from "lucide-react";
import { toast } from "sonner";
import apiClient, { API_BASE } from "./api";
import { SERVICES, PRICING_COLUMNS } from "../data/mock";

// Canonical list of services from the website price list
const CANONICAL_SERVICES = [
  ...SERVICES.map((s) => s.title),
  ...PRICING_COLUMNS.map((c) => c.label),
];

const DOWNLOADS = [
  {
    id: "bookings",
    title: "All bookings",
    desc: "Every booking with status, pet, service, date, internal notes.",
    icon: BarChart3,
    path: "/api/admin/bookings/export.csv",
    accent: "#FC5106",
  },
  {
    id: "customers",
    title: "Customers list",
    desc: "Unique customers with first/last visit, total visits & services.",
    icon: Users,
    path: "/api/admin/reports/customers.csv",
    accent: "#8B73FF",
  },
  {
    id: "missed",
    title: "Missed customers (60d+)",
    desc: "Customers who haven't returned in 60+ days. Win them back.",
    icon: AlertCircle,
    path: "/api/admin/reports/missed-customers.csv",
    accent: "#FC5106",
  },
  {
    id: "reminders",
    title: "All reminders",
    desc: "Manual + auto reminders with lead source & follow-up status.",
    icon: BellRing,
    path: "/api/admin/reports/reminders.csv",
    accent: "#8B73FF",
  },
  {
    id: "lead_sources",
    title: "Lead sources & conversions",
    desc: "Channel performance — which source converts best.",
    icon: TrendingUp,
    path: "/api/admin/reports/lead-sources.csv",
    accent: "#FC5106",
  },
  {
    id: "contacts",
    title: "Contact form messages",
    desc: "Every enquiry submitted through the website contact form.",
    icon: MessageSquare,
    path: "/api/admin/reports/contacts.csv",
    accent: "#8B73FF",
  },
];

function formatNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}

function StatCard({ label, value, icon: Icon, accent = "#FC5106", caption }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 md:p-6">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs uppercase tracking-wider text-white/50">{label}</p>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}20`, border: `1px solid ${accent}40` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <p className="font-heading text-3xl md:text-4xl font-bold text-white">{value}</p>
      {caption && <p className="text-xs text-white/50 mt-1">{caption}</p>}
    </div>
  );
}

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/admin/analytics");
      setData(res.data);
    } catch {
      toast.error("Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async (item) => {
    setDownloading(item.id);
    try {
      const token = localStorage.getItem("bf_token");
      const url = `${API_BASE.replace(/\/api$/, "")}${item.path}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `${item.id}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Could not download report");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
      </div>
    );
  }

  const t = data?.totals || {};
  const sources = data?.lead_sources || [];
  const trend = data?.monthly_trend || [];
  const maxSource = Math.max(1, ...sources.map((s) => s.count));
  const maxTrend = Math.max(1, ...trend.map((s) => s.count));

  // Build a canonical services list (synced with website price list).
  // Match backend booking counts to canonical names (case-insensitive, ignoring extra whitespace).
  const backendByService = data?.by_service || [];
  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const counted = new Map();
  backendByService.forEach((b) => counted.set(norm(b.service), b.count));
  const canonicalServices = CANONICAL_SERVICES.map((label) => ({
    service: label,
    count: counted.get(norm(label)) || 0,
  }))
    .filter((v, i, arr) => arr.findIndex((x) => norm(x.service) === norm(v.service)) === i)
    .sort((a, b) => b.count - a.count);
  const services = canonicalServices;
  const maxService = Math.max(1, ...services.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="admin-reports">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Reports & analytics</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            Business insights
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Live numbers from your studio. Download any report below.
          </p>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <StatCard
          label="Total grooming"
          value={formatNum(t.total_grooming)}
          icon={Sparkles}
          accent="#FC5106"
          caption={`${formatNum(t.this_month_grooming)} this month`}
        />
        <StatCard
          label="Upcoming"
          value={formatNum(t.upcoming)}
          icon={TrendingUp}
          accent="#8B73FF"
          caption={`${formatNum(t.confirmed)} confirmed`}
        />
        <StatCard
          label="Repeat customers"
          value={`${t.repeat_percentage ?? 0}%`}
          icon={Repeat}
          accent="#FC5106"
          caption={`${formatNum(t.repeat_customers)} of ${formatNum(t.total_customers)}`}
        />
        <StatCard
          label="Total customers"
          value={formatNum(t.total_customers)}
          icon={Users}
          accent="#8B73FF"
          caption="Unique pet parents"
        />
        <StatCard
          label="Missed (60d+)"
          value={formatNum(t.missed_customers)}
          icon={AlertCircle}
          accent={(t.missed_customers ?? 0) > 0 ? "#FC5106" : "#8B73FF"}
          caption="Reach out & win them back"
        />
        <StatCard
          label="Cancelled bookings"
          value={formatNum(t.cancelled)}
          icon={FileText}
          accent="#FC5106"
        />
      </div>

      {/* Lead sources + Top services */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="lead-sources-card">
          <div className="flex items-end justify-between mb-5">
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Lead source breakdown</h3>
              <p className="text-white/40 text-xs">Where reminders / leads come from</p>
            </div>
            <button
              onClick={() => download(DOWNLOADS.find((d) => d.id === "lead_sources"))}
              className="text-xs text-[#C9BCFF] hover:underline"
            >
              Download →
            </button>
          </div>
          {sources.length === 0 ? (
            <p className="text-white/40 text-sm py-6 text-center">
              No source data yet. Add a lead source when creating a reminder.
            </p>
          ) : (
            <ul className="space-y-3.5">
              {sources.map((s) => (
                <li key={s.source}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-white/85">{s.source}</span>
                    <span className="text-white/60 font-semibold">
                      {s.count} <span className="text-white/40">· {s.percentage}%</span>
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FC5106] to-[#8B73FF]"
                      style={{ width: `${(s.count / maxSource) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="top-services-card">
          <div className="flex items-end justify-between mb-5">
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Service performance</h3>
              <p className="text-white/40 text-xs">All services on your price list · completed bookings</p>
            </div>
          </div>
          {services.length === 0 ? (
            <p className="text-white/40 text-sm py-6 text-center">No services configured.</p>
          ) : (
            <ul className="space-y-3.5 max-h-[420px] overflow-y-auto pr-1">
              {services.map((s) => (
                <li key={s.service}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className={`truncate pr-2 ${s.count > 0 ? "text-white/85" : "text-white/45"}`}>
                      {s.service}
                    </span>
                    <span className={`font-semibold ${s.count > 0 ? "text-white/70" : "text-white/50"}`}>
                      {s.count}
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={s.count > 0 ? "h-full bg-[#FC5106]" : "h-full bg-white/5"}
                      style={{ width: `${(s.count / maxService) * 100}%`, minWidth: s.count > 0 ? 4 : 0 }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Monthly trend */}
      {trend.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="trend-card">
          <h3 className="font-heading text-lg font-bold text-white mb-1">Grooming sessions per month</h3>
          <p className="text-white/40 text-xs mb-6">Completed bookings over time</p>
          <div className="flex items-end gap-3 h-48">
            {trend.map((m) => {
              const pct = (m.count / maxTrend) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className="w-full bg-gradient-to-t from-[#FC5106] to-[#8B73FF] rounded-t-md"
                      style={{ height: `${pct}%`, minHeight: 4 }}
                      title={`${m.count} bookings`}
                    />
                  </div>
                  <p className="text-white/80 font-bold text-sm">{m.count}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider">{m.month}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reports download grid */}
      <div>
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Downloads</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Reports</h2>
            <p className="text-white/50 text-sm mt-1">All exports are CSV — open in Excel or Google Sheets.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOWNLOADS.map((d) => {
            const Icon = d.icon;
            const isDl = downloading === d.id;
            return (
              <div
                key={d.id}
                data-testid={`report-card-${d.id}`}
                className="rounded-2xl border border-white/10 bg-[#121212] p-5 hover:border-white/30 transition-colors flex flex-col"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${d.accent}20`, border: `1px solid ${d.accent}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: d.accent }} />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-1">{d.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-5 flex-1">{d.desc}</p>
                <button
                  onClick={() => download(d)}
                  disabled={isDl}
                  data-testid={`report-download-${d.id}`}
                  className="btn-secondary !py-2.5 !px-4 !text-sm w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDl ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                  ) : (
                    <><Download className="w-4 h-4" /> Download CSV</>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
